import { Connection, PublicKey } from '@solana/web3.js';
import { Provider } from 'ethers';
import { TldParserEvm } from './evm/parsers';
import { AddressAndDomain } from './evm/types/AddressAndDomain';
import { NetworkWithRpc } from './evm/utils';
import { ITldParser } from './parsers.interface';
import { NameAccountAndDomain } from './svm/name-record-handler';
import { TldParserSvm } from './svm/parsers';
import { MainDomain } from './svm/state/main-domain';
import { NameRecordHeader } from './svm/state/name-record-header';
import { NameRecord } from 'evm';
import { Record, RecordResult, AvatarOptions } from './types/records';

/**
 * TldParser class
 *
 * This class has been improved to maintain compatibility with previous versions.
 * The methods present in this class are provided for backwards compatibility
 * and to facilitate easy migration to v1 in future builds.
 *
 * The TldParser for multiple chains will be implemented, and Solana integration will remain unchanged without any breaking modifications.
 */
export class TldParser implements ITldParser {
    connection: Connection | Provider;

    constructor(connection: Connection | NetworkWithRpc, chain?: string) {
        if (new.target === TldParser) {
            return TldParser.createParser(connection, chain);
        }
    }

    private static createParser(
        connection: Connection | NetworkWithRpc,
        chain?: string,
    ): ITldParser {
        switch (chain?.toLowerCase()) {
            case 'yona':
            case 'eclipse':
            case 'termina':
            case 'solana':
            case undefined:
                return new TldParserSvm(connection as Connection);
            case 'monad':
                return new TldParserEvm(connection as NetworkWithRpc);
            default:
                throw new Error(`Unsupported TldParser chain: ${chain}`);
        }
    }

    /**
     * retrieves all nameAccounts for any user.
     *
     * @param userAccount user publickey or string
     */
    async getAllUserDomains(
        userAccount: PublicKey | string,
    ): Promise<PublicKey[] | NameRecord[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves all nameaccounts for any user in a specific tld.
     *
     * @param userAccount user publickey or string
     * @param tld tld to be retrieved from
     */
    async getAllUserDomainsFromTld(
        userAccount: PublicKey | string,
        tld: string,
    ): Promise<PublicKey[] | NameRecord[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves owner of a specific Name Account from domain.tld.
     *
     * @param domainTld full string of domain and tld e.g. "miester.poor"
     */
    async getOwnerFromDomainTld(
        domainTld: string,
    ): Promise<PublicKey | undefined | string> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves domainTld data a domain from domain.tld.
     *
     * @param domainTld full string of domain and tld e.g. "miester.poor"
     */
    async getNameRecordFromDomainTld(
        domainTld: string,
    ): Promise<NameRecordHeader | NameRecord | undefined> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves tld from parent name via TldHouse account.
     *
     * @param parentAccount parent publickey or string
     */
    async getTldFromParentAccount(
        parentAccount: PublicKey | string,
    ): Promise<string> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves domain from name account via tldParent account.
     *
     * @param nameAccount name publickey or string
     * @param parentAccountOwner parent Owner or string (TldHouse)
     */
    async reverseLookupNameAccount(
        nameAccount: PublicKey | string,
        parentAccountOwner: PublicKey | string,
    ): Promise<string> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves main domain name account and its domain tld from user address.
     *
     * @param userAddress user publickey or string
     */
    async getMainDomain(
        userAddress: PublicKey | string,
    ): Promise<MainDomain | NameRecord> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves all parsed domains as strings with name accounts in an array for user in a specific TLD.
     * in alphabetical order
     *
     * @param userAccount user publickey or string
     * @param tld tld to be retrieved from
     */
    async getParsedAllUserDomainsFromTldUnwrapped(
        userAccount: PublicKey | string,
        tld: string,
        concurrency: number = 10,
    ): Promise<NameAccountAndDomain[] | AddressAndDomain[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves all parsed domains and name accounts including NFTs in an array for any user in a specific TLD.
     * in alphabetical order
     *
     * @param userAccount user publickey or string
     * @param tld tld to be retrieved from
     */
    async getParsedAllUserDomainsFromTld(
        userAccount: PublicKey | string,
        tld: string,
        concurrency: number = 10,
    ): Promise<NameAccountAndDomain[] | AddressAndDomain[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves all parsed domains and name accounts for user.
     * in alphabetical order
     *
     * @param userAccount user publickey or string
     */
    async getParsedAllUserDomainsUnwrapped(
        userAccount: PublicKey | string,
        concurrency: number = 10,
    ): Promise<NameAccountAndDomain[] | AddressAndDomain[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * retrieves all parsed domains and name accounts including NFTs for user.
     * in alphabetical order
     *
     * @param userAccount user publickey or string
     */
    async getParsedAllUserDomains(
        userAccount: PublicKey | string,
        concurrency: number = 10,
    ): Promise<NameAccountAndDomain[] | AddressAndDomain[]> {
        throw new Error('Method not implemented.');
    }

    /**
     * Get a single record for a domain.
     *
     * @param domainTld Full domain like "miester.mon" or "miester.poor"
     * @param record The record type to fetch
     */
    async getRecord(
        domainTld: string,
        record: Record,
    ): Promise<string | null> {
        throw new Error('Method not implemented.');
    }

    /**
     * Get multiple records for a domain (batched).
     *
     * @param domainTld Full domain like "miester.mon"
     * @param records Array of record types (default: all records)
     */
    async getRecords(
        domainTld: string,
        records?: Record[],
    ): Promise<RecordResult> {
        throw new Error('Method not implemented.');
    }

    /**
     * Get resolved avatar URL (IPFS/Arweave → HTTP gateway).
     *
     * @param domainTld Full domain like "miester.mon"
     * @param options Gateway URLs for IPFS/Arweave
     */
    async getAvatar(
        domainTld: string,
        options?: AvatarOptions,
    ): Promise<string | null> {
        throw new Error('Method not implemented.');
    }

    /**
     * Get main domains for multiple addresses.
     *
     * @param addresses Array of wallet addresses
     */
    async getMainDomains(_addresses: string[]): Promise<(string | null)[]> {
        throw new Error('Method not implemented.');
    }
}
